import { z } from 'zod';
import { LEVEL_PATTERN, UNIT_PATTERN } from './naming.ts';

/**
 * `data/units.json` — the critical shape.
 *
 * The villa product is **two separate dwellings in one shell**: the villa
 * (ground + first) and the independent apartment above, which has its own
 * entrance and may sell separately. Dwellings are first-class here, not floors,
 * because status and price hang off the dwelling and the interface has to say
 * so before it shows a floor plan.
 */

export const DWELLING_STATUSES = ['available', 'reserved', 'sold'] as const;
export type DwellingStatus = (typeof DWELLING_STATUSES)[number];
export const DwellingStatusSchema = z.enum(DWELLING_STATUSES);

export const PRODUCT_TYPES = [
  'villa_with_apartment',
  'townhouse_upper',
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const LevelSchema = z.strictObject({
  code: z
    .string()
    .regex(LEVEL_PATTERN, 'level code must satisfy the naming contract'),
  nameAr: z.string().min(1),
  /** Null where the brochure does not state a count. Renders as "—", not 0. */
  bathrooms: z.number().int().min(0).nullable(),
  roomsAr: z.array(z.string().min(1)).min(1),
  /** Built area in m². Unanswered per level (§15); T1 computes it (§5B). */
  area: z.number().positive().nullish(),
});

export const DwellingSchema = z.strictObject({
  id: z.string().min(1),
  labelAr: z.string().min(1),
  status: DwellingStatusSchema,
  /** SAR. Null renders «اطلب السعر», never a crash and never a zero. */
  price: z.number().positive().nullable(),
  /**
   * True while the price is demo-mode fiction. The interface must render a
   * visible «سعر افتراضي» flag beside any price carrying this — a mocked
   * number without its label is indistinguishable from a lie.
   */
  priceIsDemo: z.boolean().optional(),
  separateEntrance: z.boolean().optional(),
  levels: z.array(LevelSchema).min(1),
});

export const UnitSchema = z.strictObject({
  id: z.string().regex(UNIT_PATTERN, 'unit id must satisfy the naming contract'),
  productType: z.enum(PRODUCT_TYPES),
  /** m². `areaBasis` says whether that is land or built — currently "TBC". */
  area: z.number().positive().optional(),
  areaBasis: z.enum(['land', 'built', 'TBC']).optional(),
  streetWidth: z.number().positive(),
  orientationAr: z.string().min(1),
  dwellings: z.array(DwellingSchema).min(1),
  /** Groups units that share geometry, so the masterplan instances (§10). */
  modelSet: z.string().min(1),
  panoramaSet: z.string().min(1).nullable(),
});

export const UnitsSchema = z.array(UnitSchema).min(1);

export type Level = z.infer<typeof LevelSchema>;
export type Dwelling = z.infer<typeof DwellingSchema>;
export type Unit = z.infer<typeof UnitSchema>;

// ---------------------------------------------------------------------------
// Derived views — used by the app, the worker and the asset tools alike
// ---------------------------------------------------------------------------

/** Every level code declared for a unit, across all of its dwellings. */
export function levelCodesOf(unit: Unit): string[] {
  const seen = new Set<string>();
  for (const dwelling of unit.dwellings) {
    for (const level of dwelling.levels) seen.add(level.code);
  }
  return [...seen];
}

export function findUnit(units: readonly Unit[], id: string): Unit | undefined {
  return units.find((u) => u.id === id);
}

export function findDwelling(
  units: readonly Unit[],
  dwellingId: string,
): { unit: Unit; dwelling: Dwelling } | undefined {
  for (const unit of units) {
    const dwelling = unit.dwellings.find((d) => d.id === dwellingId);
    if (dwelling) return { unit, dwelling };
  }
  return undefined;
}

export function findLevel(
  dwelling: Dwelling,
  code: string,
): Level | undefined {
  return dwelling.levels.find((l) => l.code === code);
}

/**
 * The dwelling a level belongs to. The villa's `L00`/`L01` and the apartment's
 * `APT` live in one shell, so a level code alone does not identify a product.
 */
export function dwellingForLevel(
  unit: Unit,
  levelCode: string,
): Dwelling | undefined {
  return unit.dwellings.find((d) =>
    d.levels.some((l) => l.code === levelCode),
  );
}

/** Unit-level rollup: available if any dwelling is. Drives the legend colour. */
export function unitStatus(unit: Unit): DwellingStatus {
  if (unit.dwellings.some((d) => d.status === 'available')) return 'available';
  if (unit.dwellings.some((d) => d.status === 'reserved')) return 'reserved';
  return 'sold';
}

/** Distinct model sets across the catalogue — the set the pipeline must bake. */
export function modelSetsOf(units: readonly Unit[]): Map<string, Unit[]> {
  const sets = new Map<string, Unit[]>();
  for (const unit of units) {
    const bucket = sets.get(unit.modelSet);
    if (bucket) bucket.push(unit);
    else sets.set(unit.modelSet, [unit]);
  }
  return sets;
}

/**
 * The unit whose geometry is authored for a model set. First declaration wins,
 * deterministically, so a re-run of the pipeline never renames files.
 */
export function representativeUnitOf(
  units: readonly Unit[],
  modelSet: string,
): Unit | undefined {
  return units.find((u) => u.modelSet === modelSet);
}
